import { useState, useEffect, useRef } from 'react';
import { Header, Button, Modal, TextField, DataGrid } from '../layout';
import Swal from 'sweetalert2';
import { api } from '../services/api';
import { setDocumentTitle } from '../utils/titleUtils';

const ARTICLES_URL = `${process.env.REACT_APP_API_URL}/api/articles-datatable`;
const ARTICLES_INITIAL_SORT = { key: 'Description', dir: 'asc' };

const getArticleColumns = ({ onEdit, onDelete }) => [
    { key: 'Item', header: 'Codice', sortable: true, cellClassName: 'px-4 text-sm font-semibold text-gray-800', render: (a) => a.Item },
    { key: 'Description', header: 'Descrizione', sortable: true, cellClassName: 'px-4 text-sm text-gray-700', render: (a) => a.Description },
    { key: 'BaseUoM', header: 'UdM', sortable: true, cellClassName: 'px-4 text-sm text-gray-600', render: (a) => a.BaseUoM },
    { key: 'PriceList', header: 'Listino', sortable: true, cellClassName: 'px-4 text-sm text-gray-600', render: (a) => a.PriceList },
    {
        key: 'Price',
        header: 'Prezzo',
        sortable: true,
        cellClassName: 'px-4 text-sm text-gray-800 tabular-nums whitespace-nowrap',
        render: (a) => (a.Price === null || a.Price === undefined ? '' : `${parseFloat(a.Price).toFixed(2)} € / ${a.PriceListUoM || ''}`)
    },
    {
        key: 'ValidityEndingDate',
        header: 'Validità',
        sortable: true,
        cellClassName: 'px-4 text-sm text-gray-600 whitespace-nowrap',
        render: (a) => {
            if (!a.ValidityStartingDate || !a.ValidityEndingDate) return '';
            const start = new Date(a.ValidityStartingDate).toLocaleDateString('it-IT');
            const end = new Date(a.ValidityEndingDate).toLocaleDateString('it-IT');
            return `${start} - ${end}`;
        }
    },
    {
        key: 'actions',
        header: 'Azioni',
        sortable: false,
        align: 'center',
        cellClassName: 'px-4',
        render: (a) => (
            <div className="flex items-center justify-center gap-2">
                <button
                    onClick={() => onEdit(a)}
                    className="px-3 py-1.5 border border-blue-500 text-blue-600 rounded-lg text-xs font-semibold hover:bg-blue-50 transition-colors"
                >
                    Modifica
                </button>
                <button
                    onClick={() => onDelete(a)}
                    className="px-3 py-1.5 border border-red-500 text-red-600 rounded-lg text-xs font-semibold hover:bg-red-50 transition-colors"
                >
                    Elimina
                </button>
            </div>
        )
    }
];

const ArticlesManagement = () => {
    const [modalOpen, setModalOpen] = useState(false);
    const [currentArticle, setCurrentArticle] = useState(null);
    const [formData, setFormData] = useState({
        Item: '',
        Description: '',
        BaseUoM: '',
        PriceList: '',
        ValidityStartingDate: '',
        ValidityEndingDate: '',
        Price: '',
        PriceListUoM: '',
        IsRicambio: false
    });
    const [errors, setErrors] = useState({});
    const [isImporting, setIsImporting] = useState(false);
    const fileInputRef = useRef(null);
    const [reloadKey, setReloadKey] = useState(0);
    const reloadArticles = () => setReloadKey((k) => k + 1);

    useEffect(() => {
        setDocumentTitle('Gestione Articoli');
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.Item) newErrors.Item = 'Il codice articolo è obbligatorio';
        if (!formData.Description) newErrors.Description = 'La descrizione è obbligatoria';
        if (!formData.BaseUoM) newErrors.BaseUoM = 'L\'unità di misura è obbligatoria';
        if (!formData.PriceList) newErrors.PriceList = 'Il listino prezzi è obbligatorio';
        if (!formData.Price) newErrors.Price = 'Il prezzo è obbligatorio';
        else if (isNaN(formData.Price)) newErrors.Price = 'Il prezzo deve essere un numero';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleAddArticle = () => {
        setCurrentArticle(null);
        setFormData({
            Item: '',
            Description: '',
            BaseUoM: '',
            PriceList: '',
            ValidityStartingDate: '',
            ValidityEndingDate: '',
            Price: '',
            PriceListUoM: '',
            IsRicambio: false
        });
        setModalOpen(true);
    };

    const handleEditArticle = (article) => {
        setCurrentArticle(article);
        setFormData({
            Item: article.Item,
            Description: article.Description,
            BaseUoM: article.BaseUoM,
            PriceList: article.PriceList,
            ValidityStartingDate: article.ValidityStartingDate ? new Date(article.ValidityStartingDate).toISOString().split('T')[0] : '',
            ValidityEndingDate: article.ValidityEndingDate ? new Date(article.ValidityEndingDate).toISOString().split('T')[0] : '',
            Price: article.Price.toString(),
            PriceListUoM: article.PriceListUoM,
            IsRicambio: article.IsRicambio || false
        });
        setModalOpen(true);
    };

    const handleDeleteArticle = (article) => {
        Swal.fire({
            title: 'Sei sicuro?',
            text: `Vuoi eliminare l'articolo ${article.Item}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sì, elimina!',
            cancelButtonText: 'Annulla'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    // Call API to delete article
                    await api.deleteArticle(article.Item);

                    // Show success message
                    Swal.fire(
                        'Eliminato!',
                        'L\'articolo è stato eliminato.',
                        'success'
                    );

                    reloadArticles();
                } catch (error) {
                    console.error('Error deleting article:', error);
                    Swal.fire({
                        icon: 'error',
                        title: 'Errore',
                        text: error.message || 'Impossibile eliminare l\'articolo'
                    });
                }
            }
        });
    };

    const handleImportClick = () => {
        // Trigger file input click
        fileInputRef.current.click();
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: 'Il file deve essere in formato CSV'
            });
            return;
        }

        // Confirm import
        const result = await Swal.fire({
            title: 'Importare articoli?',
            text: `Stai per importare articoli dal file "${file.name}". Gli articoli esistenti verranno aggiornati. Continuare?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sì, importa!',
            cancelButtonText: 'Annulla'
        });

        if (!result.isConfirmed) {
            // Reset file input
            e.target.value = null;
            return;
        }

        try {
            setIsImporting(true);

            // Call API to import CSV
            const response = await api.importArticlesCSV(file);

            // Show success message with stats
            Swal.fire({
                icon: 'success',
                title: 'Importazione completata',
                html: `
                    <p>Righe totali: ${response.stats.total_rows}</p>
                    <p>Articoli inseriti: ${response.stats.inserted}</p>
                    <p>Articoli aggiornati: ${response.stats.updated}</p>
                    <p>Errori: ${response.stats.errors}</p>
                `
            });

            reloadArticles();
        } catch (error) {
            console.error('Error importing CSV:', error);
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: error.message || 'Impossibile importare il file CSV'
            });
        } finally {
            setIsImporting(false);
            // Reset file input
            e.target.value = null;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        // Convert price to float
        const articleData = {
            ...formData,
            Price: parseFloat(formData.Price)
        };

        try {
            if (currentArticle) {
                // Update existing article
                await api.updateArticle(currentArticle.Item, articleData);

            } else {
                // Add new article
                await api.createArticle(articleData);
            }

            setModalOpen(false);

            Swal.fire({
                icon: 'success',
                title: currentArticle ? 'Articolo aggiornato' : 'Articolo aggiunto',
                showConfirmButton: false,
                timer: 1500
            });

            reloadArticles();
        } catch (error) {
            console.error('Error saving article:', error);
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: error.message || 'Impossibile salvare l\'articolo'
            });
        }
    };


    return (
        <div className="min-h-screen bg-gray-100">
            <Header
                title="Gestione Articoli"
                backText="Dashboard"
                backTo="/dashboard"
            />

            <div className="container mx-auto px-4 py-6">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-semibold text-gray-800">Elenco Articoli</h2>
                    <div className="flex space-x-3">
                        <Button
                            onClick={handleImportClick}
                            color="secondary"
                            className="flex items-center"
                            disabled={isImporting}
                        >
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                            </svg>
                            {isImporting ? 'Importazione...' : 'Importa CSV'}
                        </Button>
                        <input
                            type="file"
                            accept=".csv"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            className="hidden"
                        />
                        <Button
                            onClick={handleAddArticle}
                            color="primary"
                            className="flex items-center"
                        >
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            Nuovo Articolo
                        </Button>
                    </div>
                </div>

                <div className="bg-white shadow-md rounded-lg overflow-hidden">
                    <DataGrid
                        columns={getArticleColumns({ onEdit: handleEditArticle, onDelete: handleDeleteArticle })}
                        rowKey="Item"
                        server={{ url: ARTICLES_URL, reloadKey }}
                        searchPlaceholder="Cerca codice, descrizione, listino..."
                        initialSort={ARTICLES_INITIAL_SORT}
                    />
                </div>
            </div>

            <Modal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                title={currentArticle ? "Modifica Articolo" : "Nuovo Articolo"}
                size="lg"
            >
                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <TextField
                            id="Item"
                            name="Item"
                            label="Codice Articolo"
                            value={formData.Item}
                            onChange={handleInputChange}
                            error={errors.Item}
                            required
                            disabled={!!currentArticle}
                        />
                        <TextField
                            id="Description"
                            name="Description"
                            label="Descrizione"
                            value={formData.Description}
                            onChange={handleInputChange}
                            error={errors.Description}
                            required
                        />
                        <TextField
                            id="BaseUoM"
                            name="BaseUoM"
                            label="Unità di Misura"
                            value={formData.BaseUoM}
                            onChange={handleInputChange}
                            error={errors.BaseUoM}
                            required
                        />
                        <TextField
                            id="PriceList"
                            name="PriceList"
                            label="Listino Prezzi"
                            value={formData.PriceList}
                            onChange={handleInputChange}
                            error={errors.PriceList}
                            required
                            helperText="PR per listino ITALIA, PR-E per listino estero"
                        />
                        <TextField
                            id="ValidityStartingDate"
                            name="ValidityStartingDate"
                            label="Inizio Validità"
                            type="date"
                            value={formData.ValidityStartingDate}
                            onChange={handleInputChange}
                            error={errors.ValidityStartingDate}
                        />
                        <TextField
                            id="ValidityEndingDate"
                            name="ValidityEndingDate"
                            label="Fine Validità"
                            type="date"
                            value={formData.ValidityEndingDate}
                            onChange={handleInputChange}
                            error={errors.ValidityEndingDate}
                        />
                        <TextField
                            id="Price"
                            name="Price"
                            label="Prezzo"
                            type="number"
                            inputMode="decimal"
                            step="0.01"
                            value={formData.Price}
                            onChange={handleInputChange}
                            error={errors.Price}
                            required
                        />
                        <TextField
                            id="PriceListUoM"
                            name="PriceListUoM"
                            label="Unità di Misura Listino"
                            value={formData.PriceListUoM}
                            onChange={handleInputChange}
                            error={errors.PriceListUoM}
                        />
                    </div>

                    <div className="mt-4">
                        <label className="flex items-center space-x-3">
                            <input
                                type="checkbox"
                                id="IsRicambio"
                                name="IsRicambio"
                                checked={formData.IsRicambio}
                                onChange={(e) => setFormData(prev => ({ ...prev, IsRicambio: e.target.checked }))}
                                className="w-4 h-4 rounded text-red-500"
                            />
                            <span className="text-sm font-medium text-gray-700">
                                È un ricambio
                            </span>
                        </label>
                    </div>

                    <div className="mt-6 flex justify-end space-x-3">
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() => setModalOpen(false)}
                        >
                            Annulla
                        </Button>
                        <Button
                            type="submit"
                            color="primary"
                        >
                            {currentArticle ? "Aggiorna" : "Salva"}
                        </Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default ArticlesManagement;